import React, { useEffect, useState } from 'react'
import { GeolocationContext, useAppProviderContext, useConfigurationContext } from '../../core/application/contexts'
import { IGeolocationProvider } from '../../core/domain/providers/IGeolocationProvider'
import { Geolocation } from '../../core/domain/models'

export interface IGeolocationManagerProps {
  children?: React.ReactNode
}

/** Provides user geolocation functionalities */
const GeolocationManager: React.FC<IGeolocationManagerProps> = ({ children }) => {

  const { geolocationProvider } = useAppProviderContext()
  const { tenantConfiguration } = useConfigurationContext()

  const [geolocation, setGeolocation] = useState<Geolocation | undefined>(undefined)
  const [isLoadingGeolocation, setIsLoadingGeolocation] = useState<boolean>(true)

  useEffect(() => {
    const performInitialLocationFetch = async () => {
      if (geolocationProvider) {
        const location = await geolocationProvider.fetchGeolocation()
        setGeolocation(location)
      }
      setIsLoadingGeolocation(false)
    }

    // If geolocation enabled in configuration, try to fetch user location
    if (tenantConfiguration) {
      if (tenantConfiguration.geolocationEnabled) {
        performInitialLocationFetch()
      } else {
        // Geolocation is disabled
        setIsLoadingGeolocation(false)
      }
    }
  }, [tenantConfiguration, geolocationProvider])

  // Called when user wants to center the map to his current location
  const updateGeolocation = () => {
    const launchUpdate = async (provider: IGeolocationProvider) => {
      setIsLoadingGeolocation(true)
      const location = await provider.fetchGeolocation()
      setGeolocation(location)
      setIsLoadingGeolocation(false)
    }
    if (geolocationProvider) {
      launchUpdate(geolocationProvider)
    }
  }

  return (
    <GeolocationContext.Provider
      value={{
        isLoadingGeolocation,
        geolocation,
        updateGeolocation
      }}
    >
      {children}
    </GeolocationContext.Provider>
  )
}

export default GeolocationManager
